import React from 'react';
import { Box, Container, Typography, useTheme } from '@mui/material';
import {
  getSectionStyles,
  pageHeaderSpacing,
  sectionSpacing,
  tightSectionSpacing,
  SectionTone,
  textMeasure,
  displayMeasure,
} from '../../shared/styles';
import { artworkFor } from '../../configuration/artwork';
import { ContentIcon, hasContentIcon } from './ContentIcon';

/**
 * The building blocks every page is assembled from, so that vertical rhythm,
 * container width and heading levels stay consistent across the site.
 */

interface SectionProps {
  children: React.ReactNode;
  tone?: SectionTone;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl';
  id?: string;
  /** Renders as <section aria-labelledby> when a heading id is given. */
  labelledBy?: string;
  /**
   * `tight` for a section that continues what is above it instead of starting
   * something new - see tightSectionSpacing.
   */
  spacing?: 'normal' | 'tight';
}

export const Section: React.FC<SectionProps> = ({
  children,
  tone = 'default',
  // `xl`, so that a desktop window is used rather than filled down the middle.
  // At `lg` the page stopped at 1200px and everything wider than that laptop
  // read as a phone layout standing in the centre of a large screen. CMS blocks
  // still pass their own width and keep it.
  maxWidth = 'xl',
  id,
  labelledBy,
  spacing = 'normal',
}) => {
  const theme = useTheme();

  return (
    <Box
      component="section"
      id={id}
      aria-labelledby={labelledBy}
      sx={{
        ...getSectionStyles(theme, tone),
        ...(spacing === 'tight' ? tightSectionSpacing : sectionSpacing),
      }}
    >
      <Container maxWidth={maxWidth}>{children}</Container>
    </Box>
  );
};

interface PageHeaderProps {
  /** Small label above the title. */
  eyebrow?: string;
  title: string;
  /**
   * Artwork drawn in place of the written title - a wordmark in the identity's
   * own letterforms. The title is still what it is announced as, so it stays
   * required: the heading is a heading whether or not the picture arrives.
   */
  titleImage?: string;
  /** The line under it, saying what the page is. */
  subtitle?: string;
  /**
   * A symbol from configuration/contentIcons.json, drawn above the title. A
   * page that names none simply opens on its title.
   */
  icon?: string;
  /** Whatever follows: an intro paragraph, a button, a list of links. */
  children?: React.ReactNode;
}

/**
 * The band a page opens with, carrying its h1.
 *
 * One component rather than a Section and a SectionHeading repeated on every
 * page: the band looks the same everywhere, so how tall it is and how its parts
 * sit is decided here once, instead of drifting page by page.
 *
 * Kept short on purpose: it names the page and gets out of the way. Anything a
 * reader stays with belongs in a section below, where it has room - the page's
 * own text, not an opening to scroll past.
 *
 * No background of its own, and no rule above the title: the site runs on one
 * background from top to bottom, and a page opens on its title. A page that
 * names a symbol gets that symbol above it - the only mark left, and it says
 * something the title does not.
 *
 * A page whose title is artwork gets none: the wordmark carries the identity
 * inside it already, and a symbol above it would be that identity twice over.
 *
 * `header` rather than `section`, and safely so: a header only becomes a banner
 * landmark as a direct child of body, and every page renders inside `main`.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  eyebrow,
  title,
  titleImage,
  subtitle,
  icon,
  children,
}) => {
  const theme = useTheme();
  // A name nothing can draw shows nothing, rather than standing the neutral
  // fallback symbol over the page's title.
  const showIcon = hasContentIcon(icon);

  return (
    <Box component="header" sx={{ ...getSectionStyles(theme), ...pageHeaderSpacing }}>
      {/* Centred, as the section headings below it are: the page has one axis
          and the title stands on it. Each part carries its own measure and is
          centred within the container rather than the container being narrowed,
          so that the axis holds whatever the title's length. */}
      <Container maxWidth="xl" sx={{ textAlign: 'center' }}>
        {!titleImage && showIcon && (
          <ContentIcon name={icon} size={44} sx={{ mb: { xs: 1, md: 1.5 } }} />
        )}
        {eyebrow && (
          <Typography variant="overline" component="p" sx={{ mb: 1, color: 'primary.dark' }}>
            {eyebrow}
          </Typography>
        )}
        {titleImage ? (
          /* Still the h1, with the title as the picture's description: that is
             what a screen reader announces and what stands there if the file
             ever fails to load, so the page keeps its heading either way.

             Capped rather than given the full width - a wordmark is a long thin
             drawing, and edge to edge it towers over the line beneath it.

             The picture is a path typed into a field at /admin, so whether a
             white export of it exists is not something this component can
             know: configuration/artwork.ts holds the pairs and answers for it.
             A picture with no pair - an editor's own upload - stands as it is
             in both views. */
          <Box
            component="h1"
            sx={{ m: 0, mb: subtitle ? 2 : 0, display: 'flex', justifyContent: 'center' }}
          >
            <Box
              component="img"
              src={artworkFor(titleImage, theme.palette.mode)}
              alt={title}
              sx={{ width: '100%', maxWidth: 720, height: 'auto', display: 'block' }}
            />
          </Box>
        ) : (
          <Typography
            variant="h1"
            component="h1"
            sx={{
              // Deliberately below the theme's h1: that size belongs to the start
              // page's hero. A page header only has to name the page.
              fontSize: 'clamp(1.875rem, 3.8vw, 2.875rem)',
              ...displayMeasure,
              mx: 'auto',
              mb: subtitle ? 1 : 0,
            }}
          >
            {title}
          </Typography>
        )}
        {subtitle && (
          <Typography variant="body1" sx={{ color: 'text.secondary', ...textMeasure, mx: 'auto' }}>
            {subtitle}
          </Typography>
        )}
        {children && <Box sx={{ mt: { xs: 2, md: 2.5 } }}>{children}</Box>}
      </Container>
    </Box>
  );
};

interface SectionHeadingProps {
  /** Small label above the heading. */
  eyebrow?: string;
  title: string;
  description?: string;
  id?: string;
  /** h2 by default; h3 where the section already sits under one. */
  component?: 'h2' | 'h3';
  /**
   * Centred by default, which is the house style. CMS blocks pass their own
   * setting through, so an editor who left-aligns a block still gets it.
   */
  align?: 'left' | 'center';
  /** Inherit the surrounding colour instead of the page accent. */
  inheritColor?: boolean;
}

export const SectionHeading: React.FC<SectionHeadingProps> = ({
  eyebrow,
  title,
  description,
  id,
  component = 'h2',
  align = 'center',
  inheritColor = false,
}) => (
  // The block itself is not capped: the heading and the description each carry
  // their own measure, and a width here on top of those only ever cut whichever
  // of the two was the wider.
  <Box sx={{ mb: { xs: 4, md: 6 }, textAlign: align }}>
    {eyebrow && (
      <Typography
        variant="overline"
        component="p"
        sx={{ mb: 1.5, color: inheritColor ? 'inherit' : 'primary.dark', opacity: inheritColor ? 0.9 : 1 }}
      >
        {eyebrow}
      </Typography>
    )}
    <Typography
      id={id}
      variant="h2"
      component={component}
      sx={{
        color: 'inherit',
        ...displayMeasure,
        mx: align === 'center' ? 'auto' : 0,
        mb: description ? 2 : 0,
      }}
    >
      {title}
    </Typography>
    {description && (
      <Typography variant="body1" sx={{ color: inheritColor ? 'inherit' : 'text.secondary', ...textMeasure, mx: align === 'center' ? 'auto' : 0 }}>
        {description}
      </Typography>
    )}
  </Box>
);

/** A responsive grid that keeps a sensible minimum card width. */
export const CardGrid: React.FC<{ children: React.ReactNode; min?: number }> = ({ children, min = 280 }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: `repeat(auto-fit, minmax(${min}px, 1fr))` },
      gap: { xs: 2.5, md: 3 },
    }}
  >
    {children}
  </Box>
);
